/**
 * Room-plate images (R23) — the imagery layer's content contract (D89).
 *
 * @remarks
 * An image is the loudest promise in the game: it offers nouns without words,
 * and the prose audit's method — read the text, extract the content words —
 * has nothing to read. The contract's answer is to make the invisible promise
 * DECLARED: every plate carries `depicts`, the item ids it shows, and the
 * loader cross-checks the declaration against the rooms that use the plate.
 * That cannot prove the painting matches the list — nothing short of a human
 * eye can — but it converts an invisible promise into a reviewable one and
 * hands the art brief a generated checklist.
 *
 * The definitions are PACK-LEVEL (a table beside `voiceLines`) because `alt`
 * and `depicts` belong to the picture, not to a room's use of it — a corridor
 * plate serving two corridors must not carry its description twice. A room
 * then names a plate through {@link conditionalImageSchema}, whose object arm
 * mirrors `conditionalTextSchema` deliberately: same `fallback` + `variants`
 * shape, so the loader's structural walk finds conditional-image nodes with
 * the same discriminant it uses for text and constant-condition checking
 * covers them for free. The payload key differs (`imageId`, not `text`)
 * because it is part of the authored contract — a generic wrapper with a
 * parameterized key would trade the author's clarity for an internal
 * abstraction nothing else needs (a THIRD conditional payload would revisit
 * that call; two is a mirror with a stated reason).
 *
 * Variants are for IDENTITY changes only — "this is a different room now",
 * never "something in this room moved" (D89's editorial line; pictures carry
 * places and light, prose carries state, knowledge, and consequence). That
 * line is authoring guidance, not a schema constraint: the mechanism is
 * general on purpose, so a pack with a day/night cycle authors variants on
 * its cycle flag and only its art brief objects.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { imageIdSchema, itemIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * R31 (D108; Calder's geometry spec, per D95(4)): where an exit sits in the
 * plate's frame. A small closed enum, not a number — a painter does not place
 * a doorway at x=0.62, and a float would imply a precision the medium does
 * not have. `behind` is the camera's back (in frame or not); `absent` means
 * the exit exists and the plate does not show it — "not shown" as a DECISION
 * rather than an omission, the same move `depicts: []` makes for a plate of
 * pure architecture.
 */
export const framePositionSchema = z.enum([
	'absent',
	'behind',
	'center',
	'center-left',
	'center-right',
	'left',
	'overhead',
	'right',
	'underfoot',
]);

/** Where an exit sits in the frame — see {@link framePositionSchema}. */
export type FramePosition = z.infer<typeof framePositionSchema>;

/**
 * The camera's facing — a cardinal, because the style block already mandates
 * one camera ("from the room's doorway or threshold, looking in") and the
 * insight that makes the geometry check SMALL is that the frame's horizontal
 * bands map onto compass directions deterministically once the facing is
 * declared. It is also the scene layer's anchored viewpoint (D96), declared
 * in the 2D round for free — the field earns its keep twice.
 */
export const viewpointFacingSchema = z.enum(['east', 'north', 'south', 'west']);

/** The camera's facing — see {@link viewpointFacingSchema}. */
export type ViewpointFacing = z.infer<typeof viewpointFacingSchema>;

/**
 * One exit's place in the frame. The token is the room's own exit vocabulary
 * (the loader checks it against every room that names the plate).
 */
export const viewpointExitSchema = z
	.strictObject({
		directionToken: nonBlankStringSchema,
		framePosition: framePositionSchema,
	})
	.readonly();

/** One exit's place in the frame — see {@link viewpointExitSchema}. */
export type ViewpointExit = z.infer<typeof viewpointExitSchema>;

/**
 * The plate's declared geometry: which way the camera looks, and where each
 * of the room's exits sits in the frame.
 *
 * @remarks
 * Completeness is the point (Calder's check 1): every exit of every room
 * naming the plate must appear here — the check that catches an author who
 * simply forgot an exit costs a plate four tokens — and every token declared
 * must be a real exit, both directions. What this deliberately cannot catch,
 * named rather than papered (his §3.4): a plate can DRAW a doorway nobody
 * declared. The schema checks declarations against the room; it cannot check
 * the painting against the declaration — that is the identical hole
 * `depicts` has, inherent to validating a picture by proxy, and human review
 * remains the only gate on it.
 */
export const viewpointSchema = z
	.strictObject({
		exits: z.array(viewpointExitSchema).min(1).readonly(),
		facing: viewpointFacingSchema,
	})
	.readonly();

/** The plate's declared geometry — see {@link viewpointSchema}. */
export type Viewpoint = z.infer<typeof viewpointSchema>;

/**
 * D266: whether a pack asset is a video — the file's type is the discriminant,
 * as the audio table's `playback` kind is for a bed and a cue, so a video
 * plate needs no field of its own. H.264 in MP4 is the one video a plate may
 * be: the format every browser that plays the shell plays.
 *
 * @param path - A pack-relative asset path
 * @returns Whether the path names a video
 */
export const isVideoAssetPath = (path: string): boolean => /\.mp4$/iu.test(path);

/**
 * One authored room plate.
 *
 * @remarks
 * `alt` is a caption of the FRAME, not an inventory of the room (D89 rule 6):
 * the prose already lists the objects, and a screen-reader user must not hear
 * the room twice. It is authored per pack, never derived from item names, in
 * the environment channel's register — and it is player-facing prose, so the
 * prose-promise audit reads it into the `promises` section.
 *
 * `depicts` may be empty: a plate of pure architecture promises nothing.
 * `src` is a pack-relative asset path — where the picture lives is the
 * pack's business, exactly as its JSON documents are.
 *
 * `reducedMotionSrc` (D263) is the still a moving plate owes, as a sprite's
 * is (D182): a plate whose file loops — a waterfall, stars going by under a
 * floor — shows this file instead to a viewer who has asked for reduced
 * motion, a frame that says the same thing. Most motion belongs in a sprite
 * over a still plate, where the moving region is small; this is for the
 * plate that moves as a whole.
 *
 * D266: a moving plate may be a video (`src` an `.mp4`: H.264, silent,
 * looping), allowed only where its animated WebP would not fit the budget —
 * a rule the pack's own tests pin, plate by plate, with a reason. A video
 * plate MUST declare `reducedMotionSrc`, because that still is three things
 * at once: the reduced-motion frame, the poster a video shows before its
 * first frame and in place of a play that never starts, and the lightbox's
 * picture. And the still must be a still.
 */
export const imageDefinitionSchema = z
	.strictObject({
		alt: nonBlankStringSchema,
		depicts: z.array(itemIdSchema).readonly().default([]),
		imageId: imageIdSchema,
		reducedMotionSrc: nonBlankStringSchema.optional(),
		src: nonBlankStringSchema,
		/** R31: the plate's declared geometry — see {@link viewpointSchema}. Optional: geometry arrives plate by plate. */
		viewpoint: viewpointSchema.optional(),
	})
	.readonly()
	.refine((image) => !isVideoAssetPath(image.src) || image.reducedMotionSrc !== undefined, {
		message:
			'a video plate declares reducedMotionSrc — its still is the poster, the reduced-motion frame and the lightbox\'s picture, so without one a viewer who cannot or will not play it sees nothing',
		path: ['reducedMotionSrc'],
	})
	.refine((image) => image.reducedMotionSrc === undefined || !isVideoAssetPath(image.reducedMotionSrc), {
		message: 'reducedMotionSrc names a video — it is the still a moving plate owes, so it must be a picture',
		path: ['reducedMotionSrc'],
	});

/** One authored room plate — see {@link imageDefinitionSchema}. */
export type ImageDefinition = z.infer<typeof imageDefinitionSchema>;

/**
 * One conditional plate choice: this image, whenever this condition holds.
 * The condition is required for the same reason `conditionalTextVariantSchema`
 * requires it — an unconditional variant would shadow the fallback.
 */
export const conditionalImageVariantSchema = z
	.strictObject({
		condition: conditionSchema,
		imageId: imageIdSchema,
	})
	.readonly();

/** One conditional plate choice — see {@link conditionalImageVariantSchema}. */
export type ConditionalImageVariant = z.infer<typeof conditionalImageVariantSchema>;

/**
 * A room's plate, possibly varying with world state — the `conditionalTextSchema`
 * shape carrying an image id (see the module remarks for why the mirror is
 * deliberate). The plain arm is the common case: most rooms have one plate
 * whatever the world is doing. `variants` is ordered, first match wins, and
 * `min(1)` for the same reason text's is — an empty object form is a plain id
 * with extra steps.
 */
export const conditionalImageSchema = z.union([
	imageIdSchema,
	z
		.strictObject({
			fallback: imageIdSchema,
			variants: z.array(conditionalImageVariantSchema).min(1).readonly(),
		})
		.readonly(),
]);

/** A room's possibly-conditional plate — see {@link conditionalImageSchema}. */
export type ConditionalImage = z.infer<typeof conditionalImageSchema>;

/**
 * A pack's declared plate aspect ratio (`width:height`, e.g. `4:3`) — one
 * ratio for the whole pack, so the layout never jumps between rooms and a
 * plate that does not conform fails review instead of shifting the prose
 * mid-session. Declared on the manifest; the pack-level refinement requires
 * it whenever the pack declares images at all.
 */
export const imageAspectRatioSchema = z
	.string()
	.regex(/^[1-9]\d*:[1-9]\d*$/u, 'An aspect ratio is "width:height" in positive integers, e.g. "4:3"');

/** A pack's declared plate aspect ratio — see {@link imageAspectRatioSchema}. */
export type ImageAspectRatio = z.infer<typeof imageAspectRatioSchema>;
